import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  type RevenueSummaryReport,
  type RevenuePeriod,
  type ReportExportFormat,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
  TimeRangePicker,
  createTimeRange,
  type TimeRange,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { TrendingUp } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

export default function RevenueSummary() {
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<RevenueSummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(() => createTimeRange('this_year'));
  const [view, setView] = useState<'week' | 'month'>('month');

  useEffect(() => {
    if (currentBusiness) {
      loadReport();
    }
  }, [currentBusiness, timeRange, view]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getRevenueSummary(
        currentBusiness.id,
        view,
        timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
        timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
        timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
      );
      if (response.data) {
        setReport(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportRevenueSummary(
      currentBusiness.id,
      exportFormat,
      view,
      timeRange.preset === 'custom' ? 'custom' : timeRange.preset,
      timeRange.preset === 'custom' ? format(timeRange.startDate, 'yyyy-MM-dd') : undefined,
      timeRange.preset === 'custom' ? format(timeRange.endDate, 'yyyy-MM-dd') : undefined
    );
  };

  const columns: ColumnDef<RevenuePeriod>[] = [
    {
      accessorKey: 'period_label',
      header: 'Period',
    },
    {
      accessorKey: 'invoice_count',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Invoices" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">{row.getValue('invoice_count')}</div>
      ),
    },
    {
      accessorKey: 'revenue',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Revenue" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatAmount(row.getValue('revenue'))}
        </div>
      ),
    },
    {
      accessorKey: 'collected',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Collected" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-green-600">
          {formatAmount(row.getValue('collected'))}
        </div>
      ),
    },
    {
      accessorKey: 'outstanding',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Outstanding" />
        </div>
      ),
      cell: ({ row }) => {
        const value = row.getValue('outstanding') as string;
        const isNonZero = parseFloat(value) > 0;
        return (
          <div className={`text-right ${isNonZero ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {formatAmount(value)}
          </div>
        );
      },
    },
  ];

  return (
    <ReportLayout
      title="Revenue Summary"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <div className="flex items-center gap-4">
          <TimeRangePicker
            value={timeRange}
            onChange={setTimeRange}
          />
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(value) => value && setView(value as 'week' | 'month')}
          >
            <ToggleGroupItem value="month" aria-label="Monthly view">
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem value="week" aria-label="Weekly view">
              Weekly
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      }
    >
      {report && (
        <div className="mb-6">
          <SummaryCardsGrid>
            <SummaryCard
              label="Total Revenue"
              value={formatAmount(report.summary.total_revenue)}
              variant="success"
            />
            <SummaryCard
              label="Total Collected"
              value={formatAmount(report.summary.total_collected)}
            />
            <SummaryCard
              label="Invoices"
              value={report.summary.invoice_count.toString()}
            />
            <SummaryCard
              label="Average Invoice"
              value={formatAmount(report.summary.avg_invoice)}
            />
          </SummaryCardsGrid>
        </div>
      )}
      <DataTable
        columns={columns}
        data={report?.data ?? []}
        loading={loading}
        emptyIcon={<TrendingUp className="h-6 w-6 text-muted-foreground" />}
        emptyTitle="No Revenue Data"
        emptyDescription="No invoices found in this period."
      />
    </ReportLayout>
  );
}
